import axios from 'axios'
import { API_URL } from '../../../../modules/auth/core/_requests'


// Request interface (orderIds passed as URL params)
export interface BulkReassignmentRequest {
  orderIds: string[]
}

// Main response interface
export interface BulkReassignmentResponse {
  success: boolean
  summary: {
    total_orders_processed: number
    total_declined_items_found: number
    successful_reassignments: number
    failed_reassignments: number
    full_fulfillment_orders: number
    partial_fulfillment_orders: number
    rejected_orders: number
    execution_time_ms: number
  }
  results: {
    full_store_fulfillment: FullFulfillmentOrder[]
    partial_store_fulfillment: PartialFulfillmentOrder[]
    rejected_orders: RejectedOrder[]
  }
}

// New API Response Interfaces
export interface FulfillmentItem {
  line_item_id: string
  product_sku: string
  product_name: string
  quantity_needed: number
  assigned_store: string
  current_status: string
  previous_location: string
}

export interface UnfulfillableItem {
  line_item_id: string
  product_sku: string
  product_name: string
  quantity_needed: number
  max_available_across_stores: number
  shortage: number
  current_status: string
  previous_location: string
}

export interface RejectedOrder {
  order_id: string
  order_name: string
  total_items: number
  total_quantity_needed: number
  reason: string
  unfulfillable_items: UnfulfillableItem[]
}

export interface FullFulfillmentOrder {
  order_id: string
  order_name: string
  total_items: number
  total_quantity_needed: number
  assigned_store: string
  items: FulfillmentItem[]
}

export interface PartialFulfillmentOrder {
  order_id: string
  order_name: string
  total_items: number
  total_quantity_needed: number
  fulfilled_items: FulfillmentItem[]
  unfulfillable_items: UnfulfillableItem[]
}

// Transformed data for UI display (only includes fields available from backend)
export interface ProcessedItem {
  id: string
  orderId: string
  sku: string
  productName: string
  quantity: number
  originalStore: string
  assignedStore: string
  status: 'completed' | 'failed'
  reason?: string
  processedAt: string
}

export interface ProcessedOrder {
  orderId: string
  customerName: string
  totalItems: number
  completedItems: number
  failedItems: number
  items: ProcessedItem[]
}

// Transform API response to UI-friendly format
export function transformReassignmentResponse(response: BulkReassignmentResponse): {
  completedItems: ProcessedItem[]
  failedItems: ProcessedItem[]
  processedOrders: ProcessedOrder[]
} {
  const completedItems: ProcessedItem[] = []
  const failedItems: ProcessedItem[] = []
  const orderMap = new Map<string, ProcessedOrder>()

  // Helper function to get or create order in map
  const getOrCreateOrder = (orderId: string, orderName: string) => {
    if (!orderMap.has(orderId)) {
      orderMap.set(orderId, {
        orderId,
        customerName: orderName,
        totalItems: 0,
        completedItems: 0,
        failedItems: 0,
        items: []
      })
    }
    return orderMap.get(orderId)!
  }

  // Process full store fulfillment orders
  response.results.full_store_fulfillment.forEach(order => {
    order.items.forEach(item => {
      const processedItem: ProcessedItem = {
        id: item.line_item_id,
        orderId: order.order_id,
        sku: item.product_sku,
        productName: item.product_name,
        quantity: item.quantity_needed,
        originalStore: item.previous_location,
        assignedStore: item.assigned_store,
        status: 'completed',
        processedAt: new Date().toISOString()
      }
      
      completedItems.push(processedItem)
      
      const orderRecord = getOrCreateOrder(order.order_id, order.order_name)
      orderRecord.items.push(processedItem)
      orderRecord.totalItems++
      orderRecord.completedItems++
    })
  })

  // Process partial store fulfillment orders
  response.results.partial_store_fulfillment.forEach(order => {
    // Process fulfilled items
    order.fulfilled_items.forEach(item => {
      const processedItem: ProcessedItem = {
        id: item.line_item_id,
        orderId: order.order_id,
        sku: item.product_sku,
        productName: item.product_name,
        quantity: item.quantity_needed,
        originalStore: item.previous_location,
        assignedStore: item.assigned_store,
        status: 'completed',
        processedAt: new Date().toISOString()
      }
      
      completedItems.push(processedItem)
      
      const orderRecord = getOrCreateOrder(order.order_id, order.order_name)
      orderRecord.items.push(processedItem)
      orderRecord.totalItems++
      orderRecord.completedItems++
    })

    // Process unfulfillable items from partial orders
    order.unfulfillable_items.forEach(item => {
      const failureReason = `Insufficient inventory (Available: ${item.max_available_across_stores}, Needed: ${item.quantity_needed})`
      
      const processedItem: ProcessedItem = {
        id: item.line_item_id,
        orderId: order.order_id,
        sku: item.product_sku,
        productName: item.product_name,
        quantity: item.quantity_needed,
        originalStore: item.previous_location,
        assignedStore: '',
        status: 'failed',
        reason: failureReason,
        processedAt: new Date().toISOString()
      }
      
      failedItems.push(processedItem)
      
      const orderRecord = getOrCreateOrder(order.order_id, order.order_name)
      orderRecord.items.push(processedItem)
      orderRecord.totalItems++
      orderRecord.failedItems++
    })
  })

  // Process rejected orders
  response.results.rejected_orders.forEach(order => {
    order.unfulfillable_items.forEach(item => {
      const processedItem: ProcessedItem = {
        id: item.line_item_id,
        orderId: order.order_id,
        sku: item.product_sku,
        productName: item.product_name,
        quantity: item.quantity_needed,
        originalStore: item.previous_location,
        assignedStore: '',
        status: 'failed',
        reason: order.reason,
        processedAt: new Date().toISOString()
      }
      
      failedItems.push(processedItem)
      
      const orderRecord = getOrCreateOrder(order.order_id, order.order_name)
      orderRecord.items.push(processedItem)
      orderRecord.totalItems++
      orderRecord.failedItems++
    })
  })

  return {
    completedItems,
    failedItems,
    processedOrders: Array.from(orderMap.values())
  }
}

// Main service function
export async function bulkReassignRejectedItems(orderIds: string[]): Promise<BulkReassignmentResponse> {
  try {
    // Convert array to comma-separated string for URL
    const orderIdsParam = orderIds.join(',')
    
    const response = await axios.put(
      `${API_URL}/api/shopify-shipping/dashboard/orders-bulk-reassignments/bulk-reassign-location/orders/${orderIdsParam}`
    )
    
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        throw new Error(error.response.data?.error || 'Failed to execute bulk reassignment')
      } else {
        throw new Error('Network error when executing bulk reassignment')
      }
    }
    throw error
  }
}

// Helper function to get order IDs from selected orders
export function extractOrderIds(selectedOrders: any[]): string[] {
  return selectedOrders.map(order => order.order_id.toString())
}